/**
 * Anya, the assistant, from the app's side.
 *
 * She is a bot like any other (apps/bots/builtin/anya): an account the
 * server's bot service runs, reachable only through an ordinary end-to-end
 * encrypted one-to-one chat. What is special is that every app opens that
 * chat by itself, so she is in everybody's list from the start — and that
 * leaving it is remembered, so she does not come back uninvited.
 *
 * Who she is comes from the server (/api/assistant), not from the build:
 * the operator can run her under a new account, or not at all, without a
 * new app being shipped. Whether this account may talk to her is the
 * operator's list too (/api/assistant/access); the app only uses the answer
 * to show the right thing where the message field would be — she checks the
 * same list herself before answering.
 */

export const ASSISTANT_NOTE = 'pine:assistant/1'

const INFO_TTL = 6 * 60 * 60 * 1000
const ACCESS_TTL = 60 * 1000
const TIMEOUT = 8000

export class Assistant {
  constructor (client) {
    this.client = client
    this._info = null
    this._access = null // { allowed, at }
    this._ensuring = null
  }

  get url () {
    return (this.client.registryUrl || '').replace(/\/+$/, '')
  }

  /** Her account id, as last heard from the server — '' when unknown. */
  get account () {
    return (this._info && this._info.enabled && this._info.account) || ''
  }

  get name () {
    return (this._info && this._info.name) || 'Аня'
  }

  isAssistant (account) {
    return !!account && account === this.account
  }

  /** The note her chat request carries: she greets in the app's language. */
  note () {
    const lang = (this.client.settings && this.client.settings.language) || 'ru'
    return ASSISTANT_NOTE + ' lang=' + lang
  }

  /**
   * Who she is. What was last known comes back at once and is refreshed in
   * the background when it is old, so the chat list never waits on this.
   */
  async info ({ refresh = false } = {}) {
    if (!this._info) {
      const node = await this.client.local.get('assistant/info').catch(() => null)
      if (node && node.value) this._info = node.value
    }
    const stale = !this._info || Date.now() - (this._info.at || 0) > INFO_TTL
    if ((refresh || stale) && this.url) {
      const fresh = await this._get('/api/assistant')
      if (fresh) {
        this._info = {
          enabled: !!fresh.enabled,
          account: fresh.enabled ? String(fresh.account || '') : '',
          name: String(fresh.name || 'Аня'),
          username: String(fresh.username || ''),
          at: Date.now()
        }
        await this.client.local.put('assistant/info', this._info).catch(() => {})
      }
    }
    return this._info
  }

  /**
   * Open the chat with her if this account has never had one and has not
   * left it. Safe to call any number of times.
   */
  ensureChat () {
    if (!this._ensuring) {
      this._ensuring = this._ensureChat().finally(() => { this._ensuring = null })
    }
    return this._ensuring
  }

  async _ensureChat () {
    const client = this.client
    if (!client.onboarded || !client.directs || !this.url) return null
    const info = await this.info({ refresh: true })
    if (!info || !info.enabled || !info.account || info.account === client.identity.id) return null

    const existing = client.directs.find(info.account)
    if (existing) return existing
    if (await client.local.get('assistant/left/' + info.account).catch(() => null)) return null

    /*
     * Only once this device knows what the account already has.
     *
     * A device signed into an existing account starts with an empty list and
     * fills it from the server's copy a moment later. Opening her chat before
     * that would make a second one beside the chat the account already had —
     * two Anyas in the list, one of them empty.
     */
    if (!client._directoryRead) {
      const result = await client.syncDirectory().catch(() => null)
      if (!client._directoryRead) return null
      if (result && client.directs.find(info.account)) return client.directs.find(info.account)
    }
    if (client.directs.find(info.account)) return client.directs.find(info.account)

    const { room } = await client.directs.open(info.account, { name: info.name, note: this.note() })
    const record = client.getSummary(room.id)
    if (record) await client._saveSummary({ ...record, assistant: true, name: record.name || info.name })
    client.emit('assistant-chat', room.id)
    return client.getSummary(room.id)
  }

  /** Remembered when her chat is deleted, so she is not brought back. */
  async left (account) {
    if (!account) return
    await this.client.local.put('assistant/left/' + account, { at: Date.now() }).catch(() => {})
  }

  /**
   * Whether this account may talk to her: true, false, or null when the
   * server could not be asked (the app then lets people type — she checks
   * anyway — rather than locking the chat over a network hiccup).
   */
  async access ({ refresh = false } = {}) {
    if (!refresh && this._access && Date.now() - this._access.at < ACCESS_TTL) return this._access.allowed
    if (!this.url) return null
    const answer = await this._get('/api/assistant/access?account=' + encodeURIComponent(this.client.identity.id))
    if (!answer) return this._access ? this._access.allowed : null
    this._access = { allowed: !!answer.allowed, pro: !!answer.pro, proUntil: Number(answer.proUntil) || 0, at: Date.now() }
    return this._access.allowed
  }

  /**
   * Pine AI Pro for this account: { pro, proUntil } (proUntil is when it
   * ends, or ended; 0 if never), or null when the server could not be asked.
   */
  async plan ({ refresh = false } = {}) {
    const allowed = await this.access({ refresh })
    if (allowed === null || !this._access) return null
    return { allowed, pro: !!this._access.pro, proUntil: this._access.proUntil || 0 }
  }

  /** What the plans give: { limits: { free, pro }, models, proModels, images }. */
  async plans () {
    if (!this.url) return null
    return this._get('/api/assistant/plans')
  }

  async _get (path) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), TIMEOUT)
    try {
      const response = await fetch(this.url + path, { signal: controller.signal, cache: 'no-store' })
      if (!response.ok) return null
      return await response.json()
    } catch {
      return null
    } finally {
      clearTimeout(timer)
    }
  }
}
